






























































































































































































































import moment from 'moment';
import { dateFormat } from '@/constants';
import { getContentArea } from '@/contentful';
import {
  ButtonGroup,
  RecentActivity,
  ContributionsBar,
  AccountBalance,
  Investment,
} from '@/components/account-components';
import { ThreeResourceBuckets } from '@/components/resources-components';
import { OpenHelp } from '@/mixins';
import {
  OpenUpdateCoverageTierScreen,
  OpenInvestmentTransferScreen,
} from '@/mixins/get-content-mixins';
import store from '@/store';
import {
  LoadingService,
  ExtendMixins,
} from '@/services';
import { EventBus } from '@/event-bus';
import {
  AccountDetailResponseModel,
  MemberAccountResponseModel,
  UserModel,
  IrsContributionLimitsResponseModel,
  CTA,
} from '@/@types/member';
import { isNil } from 'lodash';

export default ExtendMixins(
  OpenHelp,
  OpenUpdateCoverageTierScreen,
  OpenInvestmentTransferScreen,
).extend({
  name: 'AccountOverview',
  beforeRouteEnter(to, from, next) {
    const promises = [
      store.dispatch('getAccountDetail', to.params.id),
      store.dispatch('getAccountRecentActivity', to.params.id),
      store.dispatch('getIRSContributionLimits'),
      getContentArea([
        'account-column-1',
        'account-column-2',
        'account-column-3',
      ]),
      store.dispatch('getGroupMigrationDates', store.getters.user.groupKey),
    ];
    LoadingService.watchPromises(promises)
      .then(next)
      .catch(() => {
        EventBus.$emit('openModal', 'Error', {
          message: 'global_connection-error',
          action: {
            label: 'global_try-again',
            performAction() {
              location.reload();
            },
          },
          hideCancel: true,
        });
        EventBus.$emit('initialLoadDone');
      });
  },
  computed: {
    isShowInvestmentAll(): boolean {
      return this.account.allowInvestments;
    },
    isShowInvestmentBalanceOnly(): boolean {
      return !this.isShowInvestmentAll && !isNil(this.account.investmentBalance) && this.account.investmentBalance !== 0;
    },
    user(): UserModel {
      return this.$store.getters.user;
    },
    futureContributionPercentage(): string {
      return this.account.investmentFutureContributionPercentage;
    },
    account(): MemberAccountResponseModel {
      return this.$store.getters.currentAccount(this.$route.params.id);
    },
    accountDetail(): AccountDetailResponseModel {
      return this.$store.getters.accountDetail;
    },
    isNonMigrated(): boolean {
      return this.account.isMigratedInd === false;
    },
    isHSA(): boolean {
      return this.account.productType === 'HSA';
    },
    isHRA(): boolean {
      return this.account.productType === 'HRA';
    },
    isVEBA(): boolean {
      return this.account.productType === 'VEBA';
    },
    isTRA(): boolean {
      return this.account.productType === 'TRA';
    },
    isAAP(): boolean {
      return this.account.productType === 'AAP';
    },
    singleIRSLimit(): IrsContributionLimitsResponseModel[] {
      const CURRENT_YEAR = this.currentYear.toString();
      return this.activeIRSLimits.filter(
        (limit) => {
          return CURRENT_YEAR === limit.taxYear.toString();
        },
      );
    },
    irsLimits(): IrsContributionLimitsResponseModel[] {
      return this.$store.getters.irsLimits.sort((a, b) => {
        return a.taxYear < b.taxYear;
      });
    },
    activeIRSLimits(): IrsContributionLimitsResponseModel[] {
      return this.irsLimits
        .filter((limit) => {
          return moment(limit.endDate, dateFormat).isAfter(moment());
        })
        .sort((a, b) => {
          if (a.taxYear < b.taxYear) {
            return 1;
          }
          if (a.taxYear > b.taxYear) {
            return -1;
          }
          return 0;
        });
    },
    currentYear(): number {
      return moment().year();
    },
    currentContributionsAmount(): number {
      return this.accountDetail.currentTaxYearContributions
        ? this.accountDetail.currentTaxYearContributions
        : 0;
    },
    claimFilingDeadline(): string | null {
      const CLAIM_FILING_DEADLINE = `${this.$options.filters.t(
        'account-overview_label_claims-filing-deadline',
      )}:`;
      const RUNOUT_PERIOD_LABEL = this.$options.filters.t(
        'account-overview_label_runout-period-append',
      );
      if (this.account.claimFilingDeadline) {
        return `${CLAIM_FILING_DEADLINE} ${this.account.claimFilingDeadline}`;
        // tslint:disable-next-line
      } else if (this.account.runoutPeriod || this.account.runoutPeriod == 0) {
        return `${CLAIM_FILING_DEADLINE} ${
          this.account.runoutPeriod
        } ${RUNOUT_PERIOD_LABEL}`;
      }
      return null;
    },
    buttons(): CTA[] {
      const buttonGroup: CTA[] = [];
      const getReimbursed: CTA = {
        to: {
          name: 'submitClaim',
          params: {
            step: '1',
            action: 'reimburse',
            accountId: this.account.accountId,
          },
        },
        text: this.$options.filters.t([
          'accounts-button-group_action_get-reimbursed',
          'global_get-reimbursed',
        ]),
        icon: 'icon-reimburse',
        cypressKey: 'get-reimbursed',
      };
      const makeDeposit: CTA = {
        to: {name: 'makeDeposit', params: {step: '1'}},
        text: this.$options.filters.t([
          'accounts-button-group_action_make-deposit',
          'global_make-deposit',
        ]),
        icon: 'icon-deposit',
        cypressKey: 'make-deposit',
      };
      const payBill: CTA = {
        to: {
          name: 'submitClaim',
          params: {
            step: '1',
            action: 'pay-bill',
            accountId: this.account.accountId,
          },
        },
        text: this.$options.filters.t([
          'accounts-button-group_action_pay-bill',
          'global_pay-bill',
        ]),
        icon: 'icon-pay_bill',
        cypressKey: 'pay-bill',
      };
      const manageCard: CTA = {
        to: {name: 'paymentMethod'},
        text: this.$options.filters.t([
          'accounts-button-group_action_manage-card',
          'global_manage-card',
        ]),
        icon: 'icon-manage_card',
        cypressKey: 'manage-card',
      };
      const manageBanking: CTA = {
        to: {name: 'bankAccounts'},
        text: this.$options.filters.t([
          'accounts-button-group_action_manage-banking',
          'global_manage-banking',
        ]),
        icon: 'icon-banking',
        cypressKey: 'manage-banking',
      };
      if (this.isNonMigrated) {
        buttonGroup.push(getReimbursed);
      }
      if (this.isHSA && this.isNonMigrated) {
        buttonGroup.push(makeDeposit);
      }
      if (this.showPayBill && this.isNonMigrated) {
        buttonGroup.push(payBill);
      }
      if (this.account.allowCards && this.isNonMigrated) {
        buttonGroup.push(manageCard);
      }
      // MBR-020: BR-1.44
      if (this.hasNonMigrated) {
        buttonGroup.push(manageBanking);
      }
      return buttonGroup;
    },
    // MBR-020: BR-1.35/1.36
    isSuppressCoverageEdit(): boolean {
      return (this.user.isSuppressEnrollment && !this.account.hasManualEnrollment) || this.isNonMigrated;
    },
    // MBR-020: BR1.18
    showPayBill(): boolean {
      return !this.isTRA && !this.isAAP;
    },
    showIWantTo(): boolean {
      return this.buttons.length > 0;
    },
    hasNonMigrated(): boolean {
      return this.$store.getters.hasNonMigrated;
    },
  },
  methods: {
    changeElection(): void {
      this.openInvestmentTransferScreen({
        account: this.account,
        changeContribution: true,
      });
    },
    updateCoverageTier(): void {
      this.openUpdateCoverageTierScreen({accountId: this.account.accountId});
    },
  },
  components: {
    RecentActivity,
    ButtonGroup,
    ContributionsBar,
    AccountBalance,
    Investment,
    ThreeResourceBuckets,
  },
});
